import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'

let cachedFlags: Record<string, boolean> | null = null
let cacheTime = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

const PRO_FEATURES = [
  'kb', 'tickets', 'unifi_integration', 'markup_rules', 'labor_rules',
  'product_kits', 'vms_licensing', 'photo_docs', 'ipam', 'crm_pipeline',
  'ai_chat', 'asset_tracking', 'rbac_advanced', 'commission_tracking',
  'rmr_management', 'proposal_analytics', 'multi_floor_plan', 'quickbooks',
  'meraki_integration', 'api_access', 'client_portal', 'e_signatures',
  'advanced_reporting', 'webhooks',
]
const ENTERPRISE_FEATURES = [
  'workflow_automation', 'workflow_builder', 'custom_domain', 'custom_domains',
  'white_label', 'cloud_integrations', 'ai_proposals',
]

function getPlanForFeature(key: string): string {
  if (PRO_FEATURES.includes(key)) return 'Pro'
  if (ENTERPRISE_FEATURES.includes(key)) return 'Enterprise'
  return 'Starter'
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState<Record<string, boolean>>(cachedFlags || {})
  const [loading, setLoading] = useState(!cachedFlags)

  const load = useCallback(async () => {
    if (cachedFlags && Date.now() - cacheTime < CACHE_TTL) {
      setFlags(cachedFlags)
      setLoading(false)
      return
    }
    try {
      const res = await api.get<Record<string, boolean>>('/features')
      cachedFlags = res
      cacheTime = Date.now()
      setFlags(res)
    } catch {
      // Non-critical — use empty flags
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  return { flags, loading }
}

export function useFeatureFlag(featureKey: string) {
  const { flags, loading } = useFeatureFlags()
  return {
    enabled: flags[featureKey] ?? false,
    loading,
    requiredPlan: getPlanForFeature(featureKey),
  }
}

export function invalidateFeatureFlags() {
  cachedFlags = null
  cacheTime = 0
}
